<template>
  <div id="app">
    <header class="site-header">
      <router-link :to="{ path: '/', hash: '#intro' }" class="brand" aria-label="Home">FT</router-link>
      <nav class="nav" aria-label="Primary">
        <router-link class="router-link" :to="{ path: '/', hash: '#intro' }">{{ t('nav.intro') }}</router-link>

        <div class="menu products-menu" @mouseenter="openMenu" @mouseleave="scheduleCloseMenu" @keydown.escape.prevent.stop="closeMenuNow">
          <router-link :to="{ path: '/', hash: '#products' }" :class="['menu-trigger', { open: isMenuOpen }]" :aria-expanded="isMenuOpen ? 'true' : 'false'" aria-haspopup="true" @keydown.down.prevent="openMenu" @keydown.enter.prevent="openMenu">
            {{ t('nav.products') }}
            <span class="chev" aria-hidden="true">▾</span>
          </router-link>
          <div class="menu-panel" v-show="isMenuOpen" @mouseenter="openMenu" @mouseleave="scheduleCloseMenu" @focusin="openMenu" @focusout="onFocusOut">
            <router-link class="menu-item" :to="{ path: '/', hash: '#products' }" @click="closeMenuNow">{{ t('products.title') }}</router-link>

            <div class="section-item" v-for="sec in sections" :key="sec.id" @mouseenter="setActiveSection(sec.id)" @mouseleave="scheduleCloseSection">
              <button class="section-btn" @click.prevent="activeSection = activeSection === sec.id ? null : sec.id">
                {{ secTitle(sec) }}
                <span class="chev" aria-hidden="true">›</span>
              </button>
              <div class="submenu-panel" v-show="activeSection === sec.id" @mouseenter="setActiveSection(sec.id)" @mouseleave="scheduleCloseSection">
                <router-link v-for="p in sec.items" :key="p.id" class="menu-item" :to="'/products/' + p.id" @click="closeMenuNow">{{ pTitle(p) }}</router-link>
              </div>
            </div>

            <router-link class="menu-item all" to="/products" @click="closeMenuNow">{{ t('products.viewAll') }}</router-link>
          </div>
        </div>

        <router-link class="router-link" to="/documents">{{ t('nav.documents') }}</router-link>
        <router-link class="router-link" :to="{ path: '/', hash: '#about' }">{{ t('nav.about') }}</router-link>
        <router-link class="router-link" :to="{ path: '/', hash: '#contact' }">{{ t('nav.contact') }}</router-link>
      </nav>
      <div class="lang-switch" role="group" aria-label="Language toggle">
        <span class="lang-indicator" :class="{ en: locale==='en' }" aria-hidden="true"></span>
        <button
          class="lang-btn"
          :aria-pressed="locale==='zh-CN' ? 'true' : 'false'"
          :tabindex="locale==='zh-CN' ? 0 : -1"
          @click="change('zh-CN')"
          @keydown.left.prevent.stop="onLangKey('left')"
          @keydown.right.prevent.stop="onLangKey('right')"
        >{{ t('lang.zh') }}</button>
        <button
          class="lang-btn"
          :aria-pressed="locale==='en' ? 'true' : 'false'"
          :tabindex="locale==='en' ? 0 : -1"
          @click="change('en')"
          @keydown.left.prevent.stop="onLangKey('left')"
          @keydown.right.prevent.stop="onLangKey('right')"
        >{{ t('lang.en') }}</button>
      </div>
    </header>

    <router-view />

    <footer class="site-footer">
      <p>{{ t('footer.rights') }}</p>
    </footer>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, watch, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { setLocale } from './i18n'
import { products as allProducts } from './data/products'

const { t, locale } = useI18n()
const route = useRoute()

// Products dropdown state
const isMenuOpen = ref(false)
const activeSection = ref(null)
const byId = Object.fromEntries(allProducts.map(p => [p.id, p]))
const sections = [
  { id: 'cloud', zh: '云与平台', en: 'Cloud & Platform', items: [byId['quantum-cloud']] },
  { id: 'ai', zh: '人工智能', en: 'Artificial Intelligence', items: [byId['neural-engine']] },
  { id: 'edge', zh: '边缘与设备', en: 'Edge & Devices', items: [byId['edge-intelligence'], byId['holo-interface']] },
  { id: 'security', zh: '安全', en: 'Security', items: [byId['secure-fabric']] }
]
const pTitle = (p) => p[locale.value === 'zh-CN' ? 'zh' : locale.value]?.title || p.en.title
const secTitle = (sec) => sec[locale.value] || sec.en

let closeMenuTimeout = null
let closeSectionTimeout = null

function openMenu() {
  // Cancel any pending close
  if (closeMenuTimeout) {
    clearTimeout(closeMenuTimeout)
    closeMenuTimeout = null
  }
  isMenuOpen.value = true
}

function closeMenuNow() {
  if (closeMenuTimeout) {
    clearTimeout(closeMenuTimeout)
    closeMenuTimeout = null
  }
  if (closeSectionTimeout) {
    clearTimeout(closeSectionTimeout)
    closeSectionTimeout = null
  }
  isMenuOpen.value = false
  activeSection.value = null
}

function scheduleCloseMenu() {
  // Delay closing to allow mouse to move to submenu
  closeMenuTimeout = setTimeout(() => {
    isMenuOpen.value = false
    activeSection.value = null
  }, 250)
}

function setActiveSection(id) {
  // Cancel any pending section close
  if (closeSectionTimeout) {
    clearTimeout(closeSectionTimeout)
    closeSectionTimeout = null
  }
  activeSection.value = id
}

function scheduleCloseSection() {
  // Delay closing section to allow mouse to move to submenu
  closeSectionTimeout = setTimeout(() => {
    activeSection.value = null
  }, 150)
}

function onFocusOut(e) {
  const root = e.currentTarget
  if (!root.contains(e.relatedTarget)) scheduleCloseMenu()
}

function change(l) {
  if (locale.value !== l) setLocale(l)
}

function onLangKey(dir) {
  if (dir === 'left') change('zh-CN')
  else if (dir === 'right') change('en')
}

function updateBg() {
  // Only animate based on scroll on the home page; use defaults elsewhere
  const isHome = route.path === '/'
  const docEl = document.documentElement
  if (!isHome) {
    docEl.style.setProperty('--bg-h', '220')
    docEl.style.setProperty('--bg-a', '0.14')
    return
  }
  const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight)
  const p = Math.min(1, Math.max(0, window.scrollY / max))
  // Map scroll to blue hue range: light blue (200) -> deep blue (235)
  const hue = Math.round(200 + 35 * p)
  const alpha = 0.18 + 0.10 * p // brighten slightly as you scroll
  docEl.style.setProperty('--bg-h', String(hue))
  docEl.style.setProperty('--bg-a', alpha.toFixed(3))
}

onMounted(() => {
  updateBg()
  window.addEventListener('scroll', updateBg, { passive: true })
  window.addEventListener('resize', updateBg)
})

watch(() => route.path, () => {
  // Defer to ensure DOM metrics (scrollHeight/innerHeight) are current
  requestAnimationFrame(updateBg)
  closeMenuNow()
})

onUnmounted(() => {
  window.removeEventListener('scroll', updateBg)
  window.removeEventListener('resize', updateBg)
})
</script>

<style>
:root { --bg-h: 215; --bg-a: 0.20; --header-height: 60px; color-scheme: dark; }
* { box-sizing: border-box; }
html { scroll-behavior: smooth; scroll-padding-top: var(--header-height); }
body {
  margin: 0;
  /* Blue-based theme with dynamic accent driven by scroll position on the home page */
  background:
    radial-gradient(1200px 500px at 80% -20%, hsla(var(--bg-h, 215), 85%, 55%, var(--bg-a, 0.20)), transparent),
    radial-gradient(900px 400px at 20% 100%, hsla(220, 90%, 45%, 0.12), transparent),
    linear-gradient(180deg, hsl(222 50% 12%), hsl(225 55% 8%));
  transition: background 120ms linear;
}
#app {
  font-family: ui-sans-serif, system-ui, "Segoe UI", Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
  color: #dfe9ff;
}
.site-header {
  position: sticky; top: 0; z-index: 10;
  display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 1rem;
  padding: .75rem 1rem; backdrop-filter: blur(10px);
  background: linear-gradient(180deg, rgba(10,14,28,0.7), rgba(10,14,28,0.35));
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.brand { font-weight: 800; letter-spacing: 1px; color: white; text-decoration: none; background: linear-gradient(135deg,#60a5fa,#2563eb); -webkit-background-clip: text; background-clip: text; color: transparent; font-size: 1.25rem; }
.nav { display: flex; gap: 1rem; justify-content: flex-start; font-size: 1rem; }
.nav a, .nav .router-link { color: #bcd0ff; text-decoration: none; padding: .4rem .6rem; border-radius: 8px; }
.nav a:hover, .nav .router-link:hover { background: rgba(255,255,255,0.06); color: #eaf1ff; }
.lang-switch {
  position: relative; display: grid; grid-template-columns: 1fr 1fr; align-items: center;
  gap: 0; padding: 4px; border-radius: 12px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
}
.lang-indicator {
  position: absolute; top: 4px; left: 4px; height: calc(100% - 8px);
  width: calc(50% - 4px);
  border-radius: 10px;
  background: linear-gradient(135deg,#60a5fa,#2563eb);
  box-shadow: 0 10px 24px rgba(59,130,246,.30), inset 0 0 0 1px rgba(255,255,255,0.12);
  transform: translateX(0%);
  transition: transform .28s cubic-bezier(.22,.61,.36,1), box-shadow .28s ease, filter .28s ease;
}
.lang-indicator.en { transform: translateX(100%); }
.lang-btn {
  position: relative; z-index: 1; background: transparent; color: #cfe0ff;
  border: none; border-radius: 8px; padding: .4rem .6rem; cursor: pointer; font: inherit;
  transition: color .2s ease, transform .2s ease;
}
.lang-btn[aria-pressed="true"] { color: #ffffff; font-weight: 600; }
.lang-btn:hover { transform: translateY(-1px); }
.lang-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(59,130,246,.30);
  border-radius: 10px;
}

/* Products dropdown menu */
.menu { position: relative; }
.menu-trigger {
  display: inline-flex; align-items: center; gap: .35rem;
  background: transparent; color: #bcd0ff; border: none;
  border-radius: 8px; padding: .4rem .6rem; cursor: pointer;
  font: inherit;
}
.menu-trigger:hover { background: rgba(255,255,255,0.06); color: #eaf1ff; }
.menu-trigger .chev { transition: transform .15s ease; display: inline-block; }
.menu-trigger.open .chev { transform: rotate(180deg); }

.menu-panel {
  position: absolute; top: 110%; left: 0; min-width: 220px; z-index: 20;
  background: linear-gradient(180deg, rgba(18,22,40,0.95), rgba(14,18,34,0.95));
  border: 1px solid rgba(255,255,255,0.10); border-radius: 12px; padding: .5rem;
  box-shadow: 0 18px 40px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.02);
  backdrop-filter: blur(10px);
}
.menu-item {
  display: block; padding: .5rem .6rem; border-radius: 8px; color: #cfe0ff; text-decoration: none;
  font: inherit;
}
.menu-item:hover { background: rgba(255,255,255,0.06); color: #ffffff; }
.menu-item.all { margin-top: .25rem; background: linear-gradient(135deg,#60a5fa,#2563eb); color: #fff; }
.menu-item.all:hover { filter: brightness(1.05); }

/* Sections with flyout submenu */
.section-item { position: relative; }
.section-btn {
  display: flex; align-items: center; justify-content: space-between; width: 100%;
  padding: .5rem .6rem; border-radius: 8px; border: none; background: transparent; color: #cfe0ff; cursor: pointer;
  font: inherit;
}
.section-btn:hover { background: rgba(255,255,255,0.06); color: #ffffff; }

.submenu-panel {
  position: absolute; top: -0.5rem; left: calc(100% + 8px); min-width: 240px; z-index: 25;
  background: linear-gradient(180deg, rgba(18,22,40,0.95), rgba(14,18,34,0.95));
  border: 1px solid rgba(255,255,255,0.10); border-radius: 12px; padding: .5rem;
  box-shadow: 0 18px 40px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.02);
  backdrop-filter: blur(10px);
}

/* Ensure the flyout stays open when hovering inside */
.section-item:hover > .submenu-panel { display: block; }

main { display: block; }
.site-footer { text-align: center; padding: 2rem 1rem; color: #97a9df; border-top: 1px solid rgba(255,255,255,0.06); background: rgba(8,12,24,0.8); }

/* Soft fade at section edges to smooth transitions between sections */
.section { position: relative; }
.section::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  /* Two opposing vertical gradients: one from the top, one from the bottom */
  background:
    linear-gradient(180deg, rgba(10,14,28,0.6), rgba(10,14,28,0) 72px),
    linear-gradient(0deg, rgba(10,14,28,0.6), rgba(10,14,28,0) 72px);
  mix-blend-mode: normal;
}

@media (prefers-reduced-motion: reduce) {
  html { scroll-behavior: auto; }
  .glow { animation: none !important; }
  .lang-indicator { transition: none !important; }
  .lang-btn { transition: none !important; }
}
</style>
